// src/contexts/account-context.tsx
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useUser } from "@clerk/clerk-react";
import { db } from "@/services/db";
import { Account } from "@/types/metrics";
import { SAMPLE_ACCOUNT_ID, MAX_FREE_ACCOUNTS } from "@/lib/constants";
import { toast } from "@/components/ui/use-toast";
import { useAccessLevel } from "../hooks/use-access-level";
import { useBrandConfig } from "../hooks/use-brand-config";
import { filterAccountsForSelector } from "@/utils/accountFilter";
import { useOnboardingDataTransfer } from "@/lib/onboarding-transfer-utils";
import {
  extractWebAppId,
  extractSheetId,
  buildWebAppUrl,
  buildSheetUrl,
} from "@/lib/clerk-url-utils";
import { useClerkMetadataMigration } from "@/hooks/use-clerk-metadata-migration";
import { useDevMember } from "@/dev/DevMemberContext";

const DEBUG = process.env.NODE_ENV === "development";

// Helper function to prepare account data for Clerk storage
// Strips out transient state properties like isLoading and compresses URLs
function prepareAccountForClerk(account: Partial<Account>): Partial<Account> {
  if (!account) return {};

  const {
    isLoading, // Explicitly omit this transient UI state
    ...persistentFields // Keep all other fields
  } = account;

  // Compress URLs before saving to Clerk to reduce metadata size
  const compressed = { ...persistentFields };

  if (compressed.webAppUrl) {
    compressed.webAppUrl =
      extractWebAppId(compressed.webAppUrl) || compressed.webAppUrl;
  }

  if (compressed.sheetUrl) {
    compressed.sheetUrl =
      extractSheetId(compressed.sheetUrl) || compressed.sheetUrl;
  }

  return compressed;
}

interface AccountContextType {
  accounts: Account[];
  allAccounts: Account[]; // Unfiltered accounts for internal operations
  activeDexieId: string;
  activeAccount: Account | undefined;
  isLoading: boolean;
  error: string | null;
  initialized: boolean;
  switchAccount: (dexieId: string) => Promise<void>;
  addAccount: (account: Account) => Promise<void>;
  updateAccount: (account: Account, optimistic?: boolean) => Promise<void>;
  deleteAccount: (dexieId: string) => Promise<void>;
  debugSyncStatus: () => Promise<void>;
  setDummyAccountCount: (count: number) => void;
  dummyAccountCount: number;
  getRecentAccountIds: () => string[];
}

export const AccountContext = createContext<AccountContextType | null>(null);

function useAccountsInternal() {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error("useAccounts must be used within AccountProvider");
  }
  return context;
}

export function useAccounts() {
  try {
    return useAccountsInternal();
  } catch (err) {
    console.error("AccountProvider Error:", err);
    console.error(
      "Component Tree:",
      document.getElementById("root")?.innerHTML,
    );
    throw err;
  }
}

// Define a type for recent account usage tracking
interface RecentAccountUsage {
  dexieId: string;
  timestamp: string;
  accountName: string;
}

// Define a type for Clerk metadata structure
interface ClerkAccountMetadata {
  accounts?: Array<{
    dexieId: string;
    accountCID: string;
    name: string;
    businessMode: "ecomm" | "leadgen";
    currency: string;
    cogsAmount: number;
    breakEvenCpa: number;
    scriptVersion?: string; // ensure scriptVersion is part of the type if stored
  }>;
  lastActiveDexieId?: string;
  recentAccountUsage?: RecentAccountUsage[];
  [key: string]: any;
}

// Type guard to check metadata structure
function isValidMetadata(metadata: unknown): metadata is ClerkAccountMetadata {
  if (!metadata || typeof metadata !== "object") return false;
  return true;
}

// Helper function to update recent account usage list
function updateRecentAccountUsage(
  currentUsage: RecentAccountUsage[] = [],
  newDexieId: string,
  accountName: string,
  maxRecent: number = 10,
): RecentAccountUsage[] {
  const timestamp = new Date().toISOString();

  // Remove existing entry for this account if it exists
  const filteredUsage = currentUsage.filter(
    (usage) => usage.dexieId !== newDexieId,
  );

  // Add new entry at the beginning
  const newUsage: RecentAccountUsage = {
    dexieId: newDexieId,
    timestamp,
    accountName,
  };

  // Keep only the most recent entries (up to maxRecent)
  return [newUsage, ...filteredUsage].slice(0, maxRecent);
}

export function AccountProvider({ children }: { children: React.ReactNode }) {
  // Run URL migration on user login (automatic, one-time per session)
  useClerkMetadataMigration();

  const { user, isLoaded: userLoaded } = useUser();
  const { hasUnlimitedAccess: isMember, isLoaded: membershipIsLoaded } =
    useAccessLevel();

  // Dev mode: allow viewing as a test member
  const { activeTestMember, isDevMode, testMemberName } = useDevMember();
  const { brandConfig, isInitialized: brandInitialized } = useBrandConfig();
  const [allAccounts, setAllAccounts] = useState<Account[]>([]);
  const [activeDexieId, setActiveDexieId] = useState<string>(SAMPLE_ACCOUNT_ID);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [dummyAccountCount, setDummyAccountCount] = useState(0);
  const { transferDataOnSignUp } = useOnboardingDataTransfer();
  const userSignupProcessed = useRef(false);
  const isSignedIn = user !== null;
  const initRetryCount = useRef(0);

  const dummyAccounts = useMemo(() => {
    return Array.from(
      { length: dummyAccountCount },
      (_, i) =>
        ({
          dexieId: `dummy-${i}`,
          accountCID: `000-000-000${i}`,
          id: `000-000-000${i}`,
          name: `Dummy Account ${i + 1}`,
          isSampleAccount: false,
          isLoading: false,
          businessMode: "ecomm",
          currency: "USD",
          cogsAmount: 0,
          breakEvenCpa: 0,
        }) as Account,
    );
  }, [dummyAccountCount]);

  // Filter accounts based on brand configuration
  const filteredAccounts = filterAccountsForSelector(allAccounts, brandConfig);

  // Sort accounts to place sample account at the bottom
  const accounts = useMemo(() => {
    const combinedAccounts = [...filteredAccounts, ...dummyAccounts];
    return [...combinedAccounts].sort((a, b) => {
      if (a.isSampleAccount) return 1;
      if (b.isSampleAccount) return -1;
      return a.name.localeCompare(b.name);
    });
  }, [filteredAccounts, dummyAccounts]);

  // Simplified initialization and sync effect
  useEffect(() => {
    let mounted = true;

    // Initial load from local DB
    const initialLoad = async () => {
      setIsLoading(true);
      try {
        const accountsFromDexie = await db.accounts.toArray();
        const sampleInDexie = accountsFromDexie.find((a) => a.isSampleAccount);
        if (!sampleInDexie) {
          const sampleAccount = await db.ensureSampleAccount();
          accountsFromDexie.push(sampleAccount);
        }

        if (mounted) {
          setAllAccounts(accountsFromDexie);
          const lastActiveId = await db.getLastActiveAccount();
          const lastAccountExists = accountsFromDexie.some(
            (a) => a.dexieId === lastActiveId,
          );

          if (lastAccountExists) {
            setActiveDexieId(lastActiveId);
          } else {
            const firstUserAccount = accountsFromDexie.find(
              (a) => !a.isSampleAccount,
            );
            setActiveDexieId(firstUserAccount?.dexieId || SAMPLE_ACCOUNT_ID);
          }
        }
      } catch (err) {
        console.error("Failed to load initial accounts from Dexie:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load accounts",
        );
      } finally {
        if (mounted) {
          setIsLoading(false);
          setInitialized(true);
        }
      }
    };

    initialLoad();

    return () => {
      mounted = false;
    };
  }, []); // Run only once on mount

  // Clerk sync effect
  useEffect(() => {
    let mounted = true;

    const syncWithClerk = async () => {
      if (!user || !isSignedIn) {
        // If user signs out, reset to only sample account and switch to it
        const sampleAccount = await db.ensureSampleAccount();
        if (mounted) {
          setAllAccounts([sampleAccount]);
          setActiveDexieId(SAMPLE_ACCOUNT_ID);
          await db.setLastActiveAccount(SAMPLE_ACCOUNT_ID);
          if (DEBUG)
            console.log(
              "[AccountContext] 🔒 User signed out, switched to sample account for security",
            );
        }
        return;
      }

      // Sync logic
      try {
        // DEV MODE: Use test member metadata instead of real user's
        const clerkMetadata = activeTestMember
          ? activeTestMember
          : (user.unsafeMetadata as any);

        if (DEBUG && activeTestMember) {
          console.log("[AccountContext] 🔧 DEV MODE: Using test member data:", testMemberName);
        }

        // Get the separate accountSheetUrls object (used in v1.2.2+ for space optimization)
        const accountSheetUrls = clerkMetadata?.accountSheetUrls || {};
        const clerkAccounts = (clerkMetadata?.accounts || [])
          .map((acc: any) => {
            // Sheet URL can be:
            // 1. In the account object directly (acc.sheetUrl) - legacy
            // 2. In the separate accountSheetUrls object keyed by accountCID - v1.2.2+
            const sheetIdOrUrl =
              acc.sheetUrl ||
              accountSheetUrls[acc.accountCID] ||
              accountSheetUrls[acc.dexieId];
            return {
              ...acc,
              // Expand compressed URLs when reading from Clerk
              webAppUrl: buildWebAppUrl(acc.webAppUrl),
              sheetUrl: buildSheetUrl(sheetIdOrUrl),
            };
          })
          .filter((a: Account) => a && a.accountCID && !a.isSampleAccount);
        const dexieAccounts = (await db.accounts.toArray()).filter(
          (a) => !a.isSampleAccount,
        );

        const accountsToAdd = clerkAccounts.filter(
          (clerk: Account) =>
            !dexieAccounts.some(
              (dexie) => dexie.accountCID === clerk.accountCID,
            ),
        );
        const accountsToDelete = dexieAccounts.filter(
          (dexie) =>
            !clerkAccounts.some(
              (clerk: Account) => clerk.accountCID === dexie.accountCID,
            ),
        );

        if (accountsToAdd.length > 0) {
          await Promise.all(
            accountsToAdd.map((acc: Account) => db.addAccount(acc)),
          );
        }
        if (accountsToDelete.length > 0) {
          await Promise.all(
            accountsToDelete.map((acc: Account) =>
              db.deleteAccount(acc.dexieId),
            ),
          );
        }

        // Check for pending account setup from onboarding data transfer
        if (
          clerkMetadata?.pendingAccountSetup &&
          !clerkAccounts.some(
            (acc: Account) =>
              acc.accountCID === clerkMetadata.pendingAccountSetup.accountCID,
          )
        ) {
          console.log(
            "[ACCOUNT CONTEXT] Found pending account setup from onboarding transfer:",
            clerkMetadata.pendingAccountSetup,
          );

          const pendingAccount = clerkMetadata.pendingAccountSetup;
          const newAccount: Account = {
            id: pendingAccount.accountCID,
            dexieId: pendingAccount.accountCID,
            accountCID: pendingAccount.accountCID,
            name: pendingAccount.accountName,
            webAppUrl: pendingAccount.webAppUrl || "",
            sheetUrl: pendingAccount.sheetUrl || "",
            businessMode: pendingAccount.businessMode || "ecomm",
            currency: "$",
            cogsAmount: 50,
            breakEvenCpa: 25,
            isSampleAccount: false,
            scriptVersion: "0.0.0",
          };

          // Add the account from pending setup
          await db.addAccount(newAccount);

          // Update Clerk metadata to move from pending to actual accounts and clear pending
          const updatedClerkAccounts = [...clerkAccounts, newAccount];
          await user.update({
            unsafeMetadata: {
              ...clerkMetadata,
              accounts: updatedClerkAccounts,
              pendingAccountSetup: undefined, // Clear pending setup
            },
          });

          console.log(
            "✅ [ACCOUNT CONTEXT] Successfully set up account from onboarding data transfer",
          );
          console.log("✅ [ACCOUNT CONTEXT] Created account:", {
            accountCID: newAccount.accountCID,
            name: newAccount.name,
            webAppUrl: newAccount.webAppUrl,
            sheetUrl: newAccount.sheetUrl,
            businessMode: newAccount.businessMode,
          });
        }

        // After sync, reload from DB to have a single source of truth
        const finalAccounts = await db.accounts.toArray();
        if (mounted) {
          setAllAccounts(finalAccounts);
        }
      } catch (err) {
        console.error("Failed to sync accounts with Clerk:", err);
      }
    };

    if (userLoaded && brandInitialized) {
      syncWithClerk();
    }

    return () => {
      mounted = false;
    };
  }, [user, isSignedIn, userLoaded, brandInitialized, activeTestMember, testMemberName]);

  // Handle onboarding data transfer for new users
  useEffect(() => {
    if (
      userLoaded &&
      user &&
      !userSignupProcessed.current &&
      allAccounts.length > 0
    ) {
      userSignupProcessed.current = true;

      // Check if this is a new user (no existing accounts in metadata)
      const clerkMetadata = user.unsafeMetadata as ClerkAccountMetadata;
      const hasExistingAccounts =
        clerkMetadata?.accounts && clerkMetadata.accounts.length > 0;

      // Also check if we have accounts loaded locally (more reliable than metadata timing)
      const hasLocalAccounts =
        allAccounts.filter((acc) => !acc.isSampleAccount).length > 0;

      if (!hasExistingAccounts && !hasLocalAccounts) {
        console.log(
          "[ACCOUNT CONTEXT] New user detected, checking for onboarding data transfer",
        );
        transferDataOnSignUp(user).catch((error) => {
          console.error(
            "[ACCOUNT CONTEXT] Failed to transfer onboarding data:",
            error,
          );
        });
      } else if (DEBUG) {
        console.log(
          "[ACCOUNT CONTEXT] Existing user detected, skipping onboarding data transfer check",
          {
            hasExistingAccounts,
            hasLocalAccounts,
            accountCount: allAccounts.length,
          },
        );
      }
    }
  }, [user, userLoaded, transferDataOnSignUp, allAccounts]);

  // Simple account switching
  const switchAccount = async (dexieId: string) => {
    if (DEBUG) console.log("[AccountContext] Switching to account:", dexieId);
    setIsLoading(true);
    try {
      // Check in allAccounts (unfiltered) to allow switching to any valid account
      let targetAccount = allAccounts.find((acc) => acc.dexieId === dexieId);

      // If account not found in context, try to fetch from database as fallback
      if (!targetAccount) {
        if (DEBUG)
          console.log(
            "[AccountContext] Account not found in context, checking database...",
          );
        const dbAccount = await db.getAccount(dexieId);
        if (dbAccount) {
          // Refresh accounts list and try again
          const updatedAccounts = await db.accounts.toArray();
          setAllAccounts(updatedAccounts);
          targetAccount = dbAccount;
          if (DEBUG)
            console.log(
              "[AccountContext] Found account in database, refreshed context",
            );
        }
      }

      if (!targetAccount) {
        throw new Error(`Account with ID ${dexieId} not found`);
      }

      // Switch immediately - don't wait for async operations
      setActiveDexieId(dexieId);
      setIsLoading(false); // Set loading to false immediately after switching

      // Do the rest asynchronously without blocking the UI
      try {
        await db.setLastActiveAccount(dexieId);

        // Load sample data on-demand if switching to sample account
        if (dexieId === SAMPLE_ACCOUNT_ID) {
          console.log("[AccountContext] Loading sample data on-demand...");
          // Check if sample data already exists
          const existingData = await db.getAccountData(SAMPLE_ACCOUNT_ID);
          if (!existingData || Object.keys(existingData).length === 0) {
            // Load sample data lazily
            const { loadSampleDataInBackground } = await import(
              "@/lib/sample-data-utils"
            );
            loadSampleDataInBackground().catch(console.error);
          } else {
            console.log("[AccountContext] Sample data already loaded");
          }
        }
        if (isSignedIn && user) {
          // Update recent account usage tracking
          const currentMetadata = (user.unsafeMetadata ||
            {}) as ClerkAccountMetadata;
          const updatedRecentUsage = updateRecentAccountUsage(
            currentMetadata.recentAccountUsage,
            dexieId,
            targetAccount.name,
          );

          await user.update({
            unsafeMetadata: {
              ...user.unsafeMetadata,
              lastActiveDexieId: dexieId,
              recentAccountUsage: updatedRecentUsage,
            },
          });

          if (DEBUG) {
            console.log(
              "[AccountContext] Updated recent account usage:",
              updatedRecentUsage,
            );
          }
        }
      } catch (asyncErr) {
        console.error(
          "Background operations after account switch failed:",
          asyncErr,
        );
        // Don't show error to user since the switch itself succeeded
      }

      if (DEBUG)
        console.log("[AccountContext] Switched active account successfully.");
    } catch (err: any) {
      console.error("Failed to switch account:", err);
      setError(err.message || "Failed to switch account");
      setIsLoading(false);
      toast({
        title: "Switch Account Failed",
        description: err.message || "Could not switch account",
        variant: "destructive",
      });
    }
  };

  const updateAccount = useCallback(
    async (updatedAccountDetails: Account, optimistic = false) => {
      if (DEBUG)
        console.log(
          "[AccountContext] Updating account:",
          updatedAccountDetails.dexieId,
          updatedAccountDetails,
        );
      if (optimistic) {
        setAllAccounts((prevAccounts) =>
          prevAccounts.map((acc) =>
            acc.dexieId === updatedAccountDetails.dexieId
              ? { ...acc, ...updatedAccountDetails }
              : acc,
          ),
        );
      } else {
        setIsLoading(true);
      }

      try {
        await db.updateAccount(updatedAccountDetails);
        const updatedAccountsFromDexie = await db.accounts.toArray();
        const userAccounts = updatedAccountsFromDexie.filter(
          (a) => !a.isSampleAccount,
        );
        const sampleAccount =
          updatedAccountsFromDexie.find((a) => a.isSampleAccount) ||
          (await db.ensureSampleAccount());
        setAllAccounts([sampleAccount, ...userAccounts]);

        if (isSignedIn && user) {
          const currentMetadata = (user.unsafeMetadata ||
            {}) as ClerkAccountMetadata;
          let clerkAccounts = currentMetadata.accounts || [];
          clerkAccounts = clerkAccounts.map((acc) =>
            acc.dexieId === updatedAccountDetails.dexieId
              ? (prepareAccountForClerk(updatedAccountDetails) as Account)
              : acc,
          );
          // Check if the account was in Clerk, if not (e.g. sample account or error), don't add it here.
          if (
            clerkAccounts.some(
              (acc) => acc.dexieId === updatedAccountDetails.dexieId,
            )
          ) {
            await user.update({
              unsafeMetadata: { ...currentMetadata, accounts: clerkAccounts },
            });
            if (DEBUG)
              console.log(
                "[AccountContext] Account updated in Clerk metadata.",
              );
          } else if (
            DEBUG &&
            updatedAccountDetails.dexieId !== SAMPLE_ACCOUNT_ID
          ) {
            console.warn(
              "[AccountContext] Account updated in Dexie was not found in Clerk metadata for update:",
              updatedAccountDetails.dexieId,
            );
          }
        }
        if (!optimistic) {
          toast({
            title: "Account Updated",
            description: `Account '${updatedAccountDetails.name}' has been updated.`,
          });
        }
        if (DEBUG)
          console.log("[AccountContext] Account updated successfully.");
      } catch (err: any) {
        console.error("Failed to update account:", err);
        setError(err.message || "Failed to update account");
        if (!optimistic) {
          toast({
            title: "Update Failed",
            description: err.message || "Could not update account",
            variant: "destructive",
          });
        }
        // Revert optimistic update if actual update fails
        if (optimistic) {
          const originalAccounts = await db.accounts.toArray(); // Fetch the true state from DB
          const userAccounts = originalAccounts.filter(
            (a) => !a.isSampleAccount,
          );
          const sampleAccount =
            originalAccounts.find((a) => a.isSampleAccount) ||
            (await db.ensureSampleAccount());
          setAllAccounts([sampleAccount, ...userAccounts]);
        }
      } finally {
        if (!optimistic) setIsLoading(false);
      }
    },
    [isSignedIn, user, toast],
  );

  const addAccount = useCallback(
    async (newAccount: Account) => {
      if (!membershipIsLoaded) {
        // This case should ideally be handled by disabling UI elements until membershipIsLoaded is true.
        // For now, we'll throw an error to prevent adding accounts with uncertain membership status.
        console.warn(
          "[AccountContext] Attempted to add account before membership status was loaded.",
        );
        throw new Error(
          "Membership status not loaded. Please try again shortly.",
        );
      }

      // Prevent adding duplicates
      const existing = accounts.find(
        (a) => a.accountCID === newAccount.accountCID,
      );
      if (existing && !newAccount.isLoading) {
        // Allow update if it's just to remove the loading flag
        const msg = `Duplicate account CID: ${newAccount.accountCID}. Please be patient & refresh the screen, then refresh the data for the new account.`;
        console.error("Failed to add account:", msg);
        toast({
          variant: "destructive",
          title: "Duplicate Account",
          description: `Account with ID ${newAccount.accountCID} already exists.`,
        });
        // Don't throw an error, just prevent the action.
        return;
      }

      const canAddMore =
        accounts.filter((a) => !a.isSampleAccount).length < MAX_FREE_ACCOUNTS;

      if (DEBUG) {
        console.log("[AccountContext] addAccount - Membership Status:", {
          isMember,
          userAccountsCount: accounts.length,
          MAX_FREE_ACCOUNTS,
        });
      }

      // Allow adding accounts if user is a member OR they are under the free limit
      if (isMember || canAddMore) {
        if (DEBUG)
          console.log("[AccountContext] Accepting to add account:", newAccount);

        await db.addAccount(newAccount);
        const updatedAccountsFromDexie = await db.accounts.toArray();
        const userAccounts = updatedAccountsFromDexie.filter(
          (a) => !a.isSampleAccount,
        );
        const sampleAccount =
          updatedAccountsFromDexie.find((a) => a.isSampleAccount) ||
          (await db.ensureSampleAccount()); // Should always exist
        setAllAccounts([sampleAccount, ...userAccounts]);

        if (isSignedIn && user) {
          const currentMetadata = (user.unsafeMetadata ||
            {}) as ClerkAccountMetadata;
          const clerkAccounts = currentMetadata.accounts || [];
          // Ensure no duplicates in Clerk either before adding
          if (
            !clerkAccounts.some(
              (acc) => acc.accountCID === newAccount.accountCID,
            )
          ) {
            const updatedClerkAccounts = [
              ...clerkAccounts,
              prepareAccountForClerk(newAccount) as Account,
            ];
            await user.update({
              unsafeMetadata: {
                ...currentMetadata,
                accounts: updatedClerkAccounts,
              },
            });
            if (DEBUG)
              console.log("[AccountContext] Account added to Clerk metadata.");
          } else {
            if (DEBUG)
              console.warn(
                "[AccountContext] Account already in Clerk metadata, not re-adding.",
                newAccount.accountCID,
              );
          }
        }
        toast({
          title: "Account Added",
          description: `Account '${newAccount.name}' has been successfully added.`,
        });
        if (DEBUG)
          console.log(
            "[AccountContext] Account added successfully:",
            newAccount,
          );
      } else {
        const msg = "You have reached the maximum number of free accounts.";
        console.warn(msg);
        toast({
          variant: "destructive",
          title: "Account Limit Reached",
          description: msg,
        });
      }
    },
    [allAccounts, isSignedIn, user, toast, membershipIsLoaded, isMember],
  );

  const debugSyncStatus = useCallback(async () => {
    if (!user || !isSignedIn) {
      toast({
        title: "Not Signed In",
        description: "Cannot sync status without a user session.",
      });
      return;
    }
    if (DEBUG)
      console.log("[AccountContext] Debug Sync: Fetching current states...");
    const clerkMetadata = (user.unsafeMetadata || {}) as ClerkAccountMetadata;
    const clerkAccountsRaw = clerkMetadata.accounts || [];
    const clerkAccounts = clerkAccountsRaw
      .map(prepareAccountForClerk)
      .filter((a) => !a.isSampleAccount);

    const dexieAccountsRaw = await db.accounts.toArray();
    const dexieAccounts = dexieAccountsRaw
      .map(prepareAccountForClerk)
      .filter((a) => !a.isSampleAccount);

    console.log(
      "--- DEBUG CLERK ACCOUNTS ---",
      JSON.stringify(clerkAccounts, null, 2),
    );
    console.log(
      "--- DEBUG DEXIE ACCOUNTS ---",
      JSON.stringify(dexieAccounts, null, 2),
    );

    const clerkOnly = clerkAccounts.filter(
      (ca) => !dexieAccounts.some((da) => da.accountCID === ca.accountCID),
    );
    const dexieOnly = dexieAccounts.filter(
      (da) => !clerkAccounts.some((ca) => ca.accountCID === da.accountCID),
    );

    let discrepancies: any[] = [];
    clerkAccounts.forEach((ca) => {
      const da = dexieAccounts.find((d) => d.accountCID === ca.accountCID);
      if (da && JSON.stringify(ca) !== JSON.stringify(da)) {
        discrepancies.push({ type: "mismatch", clerk: ca, dexie: da });
      }
    });

    const summary = {
      totalClerk: clerkAccounts.length,
      totalDexie: dexieAccounts.length,
      clerkOnly: clerkOnly.length,
      dexieOnly: dexieOnly.length,
      mismatches: discrepancies.length,
    };

    console.log("--- DEBUG SYNC SUMMARY ---", summary);
    if (clerkOnly.length) console.log("Clerk Only Details:", clerkOnly);
    if (dexieOnly.length) console.log("Dexie Only Details:", dexieOnly);
    if (discrepancies.length) console.log("Mismatch Details:", discrepancies);

    toast({
      title: "Debug Sync Status Logged",
      description: `Clerk: ${summary.totalClerk}, Dexie: ${summary.totalDexie}. Check console for details.`,
    });

    // Simple reconciliation: Prioritize Clerk as source of truth for existence,
    // but if Dexie has an account not in Clerk, it might be an orphaned local one.
    // For this debug, we mainly log. A real sync would be more complex.
  }, [user, isSignedIn, toast]);

  const deleteAccount = useCallback(
    async (dexieId: string) => {
      if (dexieId === SAMPLE_ACCOUNT_ID) {
        toast({
          title: "Cannot Delete Sample",
          description: "The sample account cannot be deleted.",
          variant: "destructive",
        });
        return;
      }
      if (DEBUG) console.log("[AccountContext] Deleting account:", dexieId);
      setIsLoading(true);
      try {
        const accountToDelete = allAccounts.find(
          (acc) => acc.dexieId === dexieId,
        );

        await db.deleteAccount(dexieId);
        const remainingAccounts = allAccounts.filter(
          (acc) => acc.dexieId !== dexieId,
        );
        setAllAccounts(remainingAccounts);

        if (activeDexieId === dexieId) {
          const newActiveId =
            remainingAccounts.find((acc) => !acc.isSampleAccount)?.dexieId ||
            SAMPLE_ACCOUNT_ID;
          setActiveDexieId(newActiveId);
          await db.setLastActiveAccount(newActiveId);
          if (isSignedIn && user) {
            await user.update({
              unsafeMetadata: {
                ...user.unsafeMetadata,
                lastActiveDexieId: newActiveId,
              },
            });
          }
        }

        if (isSignedIn && user && accountToDelete) {
          const currentMetadata = (user.unsafeMetadata ||
            {}) as ClerkAccountMetadata;
          const clerkAccounts = (currentMetadata.accounts || []).filter(
            (acc) => acc.accountCID !== accountToDelete.accountCID,
          );
          await user.update({
            unsafeMetadata: { ...currentMetadata, accounts: clerkAccounts },
          });
          if (DEBUG)
            console.log(
              "[AccountContext] Account removed from Clerk metadata using accountCID:",
              accountToDelete.accountCID,
            );
        } else if (isSignedIn && user && !accountToDelete) {
          if (DEBUG)
            console.warn(
              "[AccountContext] Could not find account details for deletion from Clerk. Clerk metadata might not be updated for dexieId:",
              dexieId,
            );
        }

        toast({
          title: "Account Deleted",
          description: `Account '${accountToDelete?.name || dexieId}' has been deleted.`,
        });
        if (DEBUG)
          console.log("[AccountContext] Account deleted successfully.");
      } catch (err: any) {
        console.error("Failed to delete account:", err);
        setError(err.message || "Failed to delete account");
        toast({
          title: "Delete Failed",
          description: err.message || "Could not delete account",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [allAccounts, activeDexieId, isSignedIn, user, toast],
  );

  const activeAccount = useMemo(
    () => allAccounts.find((acc) => acc.dexieId === activeDexieId),
    [allAccounts, activeDexieId],
  );

  // Helper function to get recent account IDs for preemptive data loading
  const getRecentAccountIds = useCallback((): string[] => {
    if (!isSignedIn || !user) return [];

    const metadata = (user.unsafeMetadata || {}) as ClerkAccountMetadata;
    const recentUsage = metadata.recentAccountUsage || [];

    // Return recent account IDs that still exist in our accounts
    return recentUsage
      .map((usage) => usage.dexieId)
      .filter((dexieId) => allAccounts.some((acc) => acc.dexieId === dexieId))
      .slice(0, 5); // Limit to 5 most recent
  }, [isSignedIn, user, allAccounts]);

  const value = useMemo(
    () => ({
      accounts,
      allAccounts: [...allAccounts, ...dummyAccounts],
      activeDexieId,
      activeAccount,
      isLoading,
      error,
      initialized,
      switchAccount,
      addAccount,
      updateAccount,
      deleteAccount,
      debugSyncStatus,
      setDummyAccountCount,
      dummyAccountCount,
      getRecentAccountIds,
    }),
    [
      accounts,
      allAccounts,
      activeDexieId,
      activeAccount,
      isLoading,
      error,
      initialized,
      switchAccount,
      addAccount,
      updateAccount,
      deleteAccount,
      debugSyncStatus,
      setDummyAccountCount,
      dummyAccountCount,
      getRecentAccountIds,
    ],
  );

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-lg font-semibold mb-2">
            Failed to load accounts. Give it a few seconds & try refreshing the
            browser
          </h2>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!initialized || isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-lg font-semibold mb-2">Loading accounts...</h2>
        </div>
      </div>
    );
  }

  return (
    <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
  );
}
